import { useState, useEffect, Suspense } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Layout,
  Typography,
  Breadcrumb,
  Button,
  Tabs,
  Card,
  Modal,
  Space,
  message
} from 'antd'
import {
  HomeOutlined,
  ProjectOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined
} from '@ant-design/icons'
import { useAuth } from '../../contexts/AuthContext'
import { useProjects } from '../../hooks/useProjects'
import TaskList from '../../components/Tasks/TaskList'
import TaskForm from '../../components/Tasks/TaskForm'
import { getTaskById, deleteTask, updateTaskStatus } from '../../services/taskApi'
import KanbanBoard from '../../components/Projects/KanbanBoard'

const { Header, Content } = Layout
const { Title, Text } = Typography
const { TabPane } = Tabs

const ProjectDetail = () => {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const projectsHook = useProjects({ autoFetch: false })
  const { getProjectById } = projectsHook

  const [projectData, setProjectData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [componentError, setComponentError] = useState(null)

  // Task modal state
  const [selectedTask, setSelectedTask] = useState(null)
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [users, setUsers] = useState([])

  // Fetch project data
  useEffect(() => {
    let isMounted = true
    const fetchProjectData = async () => {
      if (!projectId || !getProjectById) return
      try {
        if (isMounted) { setLoading(true); setError(null) }
        const project = await getProjectById(projectId)
        if (isMounted) { setProjectData(project); setLoading(false) }
      } catch (err) {
        if (isMounted) { setError(err.message || 'ไม่สามารถดึงข้อมูลโครงการได้'); setLoading(false) }
      }
    }
    fetchProjectData()
    return () => { isMounted = false }
  }, [projectId, getProjectById])

  // Fetch users (mock or real API)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const mockUsers = [{ user_id: 2, full_name: 'Admin User', username: 'admin' }]
        setUsers(mockUsers)
      } catch (err) {
        console.error(err)
      }
    }
    fetchUsers()
  }, [])

  // Task handlers
  const handleTaskSelect = (task) => setSelectedTask(task)
  const handleAddTask = () => { setSelectedTask(null); setIsEditing(false); setShowTaskForm(true) }
  const handleEditTask = (task) => { setSelectedTask(task); setIsEditing(true); setShowTaskForm(true) }
  const handleCloseForm = () => { setShowTaskForm(false); setSelectedTask(null); setIsEditing(false) }
  const handleTaskSave = () => { setShowTaskForm(false); setSelectedTask(null); setIsEditing(false); setRefreshKey(prev => prev + 1) }
  const handleDeleteTask = async (task) => {
    Modal.confirm({
      title: `ลบงาน "${task.task_title}"`,
      content: 'คุณต้องการลบงานนี้ใช่หรือไม่?',
      okText: 'ลบ', cancelText: 'ยกเลิก', okType: 'danger',
      onOk: async () => {
        try {
          const response = await deleteTask(task.id)
          if (response.success) { message.success('ลบงานเรียบร้อยแล้ว'); setRefreshKey(prev => prev + 1) }
          else message.error(response.message || 'ไม่สามารถลบงานได้')
        } catch (err) { console.error(err); message.error('เกิดข้อผิดพลาดในการลบงาน') }
      }
    })
  }
  const handleStatusChange = async (task, newStatus) => {
    try {
      const response = await updateTaskStatus(task.id, newStatus)
      if (response.success) setRefreshKey(prev => prev + 1)
      else message.error(response.message || 'ไม่สามารถอัปเดตสถานะได้')
    } catch (err) { console.error(err); message.error('เกิดข้อผิดพลาดในการอัปเดตสถานะ') }
  }

  const handleBackToProjects = () => navigate('/projects')

  if (componentError) return (
    <div style={{ padding: 24 }}>
      <Title level={4} type="danger">เกิดข้อผิดพลาด</Title>
      <p>{componentError}</p>
      <Button type="primary" onClick={handleBackToProjects}>กลับไปหน้าโครงการ</Button>
    </div>
  )

  if (loading) return (
    <div style={{ padding: 24 }}><Title level={4}>กำลังโหลดข้อมูล...</Title></div>
  )

  if (!projectData) return (
    <div style={{ padding: 24, textAlign: 'center' }}>
      <Title level={4} type="danger">ไม่พบข้อมูลโครงการ</Title>
      <Button type="primary" onClick={handleBackToProjects}>กลับไปหน้าโครงการ</Button>
    </div>
  )

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Breadcrumb */}
      <div style={{ backgroundColor: '#fff', padding: '12px 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Breadcrumb>
          <Breadcrumb.Item href="/"><HomeOutlined /> หน้าหลัก</Breadcrumb.Item>
          <Breadcrumb.Item href="/projects"><ProjectOutlined /> จัดการโครงการ</Breadcrumb.Item>
          <Breadcrumb.Item>{projectData.project_name}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      {/* Header */}
      <Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBackToProjects}>กลับ</Button>
          <div>
            <Title level={3} style={{ margin: 0 }}>{projectData.project_name}</Title>
            {projectData.project_code && <div style={{ color: '#666', fontSize: 14 }}>รหัสโครงการ: {projectData.project_code}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, color: '#666', fontSize: 14 }}>
          <span>สถานะ: {getStatusText(projectData.status)}</span>
          <span>ความคืบหน้า: {projectData.progress_percentage || 0}%</span>
          {projectData.manager_name && <span>ผู้จัดการ: {projectData.manager_name}</span>}
        </div>
      </Header>

      <Content style={{ padding: '24px' }}>
        <Tabs defaultActiveKey="1" type="card">
          {/* Tab 1: จัดการงาน */}
          <TabPane tab="จัดการงาน" key="1">
            <Card style={{ marginBottom: 24 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTask}>สร้างงานใหม่</Button>
              <Button style={{ marginLeft: 8 }} icon={<ReloadOutlined />} onClick={() => setRefreshKey(prev => prev + 1)}>รีเฟรช</Button>
            </Card>
            <TaskList
              key={refreshKey}
              projectId={projectId}
              onTaskSelect={handleTaskSelect}
              onEdit={handleEditTask}
              onDelete={handleDeleteTask}
              onStatusChange={handleStatusChange}
            />
            <Modal
              title={isEditing ? 'แก้ไขงาน' : 'สร้างงานใหม่'}
              open={showTaskForm}
              onCancel={handleCloseForm}
              footer={null}
              width={800}
              destroyOnClose
            >
              <TaskForm
                projectId={projectId}
                task={selectedTask}
                isEditing={isEditing}
                users={users}
                onSave={handleTaskSave}
                onCancel={handleCloseForm}
              />
            </Modal>
          </TabPane>

          {/* Tab 2: Kanban */}
          <TabPane tab="Kanban Board" key="2">
            <Suspense fallback={<div style={{ padding: 24, textAlign: 'center' }}><Title level={4}>กำลังโหลด Kanban Board...</Title></div>}>
              <KanbanBoard projectId={projectId} projectInfo={projectData} />
            </Suspense>
          </TabPane>
        </Tabs>
      </Content>
    </Layout>
  )
}

// Helper function สำหรับแปลงสถานะ
const getStatusText = (status) => {
  const statusMap = {
    planning: 'วางแผน',
    active: 'กำลังดำเนินการ',
    on_hold: 'พักชั่วคราว',
    completed: 'เสร็จสิ้น',
    cancelled: 'ยกเลิก'
  }
  return statusMap[status] || status
}

export default ProjectDetail
